import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import ConfigCard from '../shared/ConfigCard';
import useUsers from '../../hooks/useUsers';
import useAttackStatuses from '../../hooks/useAttackStatuses';

function AttacksTab() {
  const { users: currentUsers } = useUsers();
  const { 
    attackStatuses, 
    userStatuses,
    error: statusError,
    updateStatus 
  } = useAttackStatuses();
  
  const [loadingAttacks, setLoadingAttacks] = React.useState({});
  const [attackError, setAttackError] = React.useState(null);
  const [expandedAttacks, setExpandedAttacks] = React.useState({});

  const attacksRequiringUsers = ['Dcsync', 'ASREProast', 'Kerberoasting', 'ReadGMSAPassword'];

  const handleAttackToggle = async (attackName, userName = null) => {
    setAttackError(null);
    const isCurrentlyEnabled = userName 
      ? userStatuses[attackName]?.[userName]?.enabled
      : attackStatuses[attackName]?.enabled;

    setLoadingAttacks(prev => ({ 
      ...prev, 
      [userName ? `${attackName}-${userName}` : attackName]: true 
    }));

    const success = await updateStatus(attackName, !isCurrentlyEnabled, userName);
    
    if (!success) {
      setAttackError(`Failed to ${isCurrentlyEnabled ? 'disable' : 'enable'} ${attackName}${userName ? ` for user ${userName}` : ''}`);
    }
    
    setLoadingAttacks(prev => ({ 
      ...prev, 
      [userName ? `${attackName}-${userName}` : attackName]: false 
    }));
  };

  const toggleExpand = (attackName) => {
    setExpandedAttacks(prev => ({
      ...prev,
      [attackName]: !prev[attackName]
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Attack Configurations
      </Typography>
      
      {(attackError || statusError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {attackError || statusError}
        </Alert>
      )}
      
      {/* Attacks that don't require users */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Network-based Attacks
      </Typography>
      {Object.keys(attackStatuses)
        .filter(attackName => !attacksRequiringUsers.includes(attackName))
        .map((attackName) => (
          <ConfigCard
            key={attackName}
            title={attackName}
            enabled={attackStatuses[attackName]?.enabled || false}
            loading={loadingAttacks[attackName]}
            onToggle={() => handleAttackToggle(attackName)}
          />
        ))}

      {/* Attacks that require users */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        User-based Attacks
      </Typography>
      {Object.keys(userStatuses)

        .filter(attackName => attacksRequiringUsers.includes(attackName))
        .map((attackName) => (
          <ConfigCard
            key={attackName}
            title={attackName}
            expanded={expandedAttacks[attackName]}
            onExpand={() => toggleExpand(attackName)}
            showExpandButton={true}
            showToggle={false}
          >
            <Box sx={{ mt: 2 }}>
              {currentUsers.map((user) => (
                <ConfigCard
                  key={`${attackName}-${user.Name}`}
                  title={user.Name}
                  enabled={userStatuses[attackName]?.[user.Name]?.enabled || false}
                  loading={loadingAttacks[`${attackName}-${user.Name}`]}
                  onToggle={() => handleAttackToggle(attackName, user.Name)}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
          </ConfigCard>
        ))}
    </Box>
  );
}

export default AttacksTab; 